import React, { Fragment } from 'react';
import { Button, Col, Container, Fade, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import {
    getCameraClasses,
    createCameraClass,
    updateCameraClass,
    deleteCameraClass
} from '../api';
import editIcon from '../images/edit_icon.svg';
import CommonMetaTag from './common-meta-tag';
import Dialog from 'react-bootstrap-dialog';
import { useTranslation } from 'react-i18next';

const CameraClassesTab = () => {
    const { t } = useTranslation();
    const token = useSelector(s => s.login?.token);
    const [list, setList] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [editing, setEditing] = React.useState(null);
    const dialogRef = React.useRef(null);

    const showError = (message, title = 'Error') => {
        if (!dialogRef.current) return;
        dialogRef.current.show({
          title,
          body: message || 'Operation failed.',
          actions: [Dialog.Action('Close', () => dialogRef.current.hide(), 'btn btn-secondary')],
          bsSize: 'small'
        });
      };

    const load = React.useCallback(async () => {
        setLoading(true);
        const { status, json } = await getCameraClasses(token);
        if (status === 200 && Array.isArray(json)) setList(json); else showError(json?.message || json?.error || 'Load failed', 'Load failed');
        setLoading(false);
    }, [token]);

    React.useEffect(() => { if (token) load(); }, [token, load]);

    const save = async (values, helpers) => {
        const payload = {
            name: String(values.name || '').trim(),
            description: (values.description == null || String(values.description).trim() === '') ? null : String(values.description)
        };
        const res = editing
            ? await updateCameraClass({ id: editing.id, ...payload }, token)
            : await createCameraClass(payload, token);

        if (res.status === 200 || res.status === 201) {
            await load();
            helpers.resetForm();
            setEditing(null);
        } else {
            showError(res?.json?.message || res?.json?.error || 'Save failed', 'Save failed');
        }
        helpers.setSubmitting(false);
    };

    const remove = (id) => {
        if (!dialogRef.current) return;
        dialogRef.current.show({
            title: t("DELETE_CONFIRMATION"),
            body: t("DELETE_ITEM_QUESTION"),
          actions: [
            Dialog.Action(
                t("DELETE"),
              async () => {
                try {
                  const res = await deleteCameraClass(id, token);
                  if (res.status === 200) {
                    await load();
                    dialogRef.current.hide();
                  } else {
                    const msg = res?.json?.message || res?.json?.error || 'Delete failed';
                    dialogRef.current.show({
                      title: 'Error',
                      body: msg,
                      actions: [Dialog.Action('Close', () => dialogRef.current.hide(), 'btn btn-secondary')],
                      bsSize: 'small'
                    });
                  }
                } catch (e) {
                  showError('Unexpected error while deleting the item.');
                }
              },
              'btn btn-secondary'
            ),
            Dialog.Action('No', () => dialogRef.current.hide(), 'btn btn_red_outline_rp')
          ],
          bsSize: 'small',
          onHide: (dlg) => dlg.hide()
        });
      };

    const initial = editing ? {
        name: editing?.name ?? '',
        description: editing?.description ?? '',
    } : { name: '', description: '' };

    return (
        <Fragment>
            <CommonMetaTag />
            <Fade in appear style={{ transition: 'opacity 0.30s linear', marginTop: 100, marginBottom: 100 }}>
                <Container>
                    <div style={{ marginTop: 40, marginBottom: 80 }}>
                        <Row>
                            <Col md={5}>
                                <h4 style={{ marginBottom: 16 }}>{editing ? t("EDIT_CAMERA_CLASS") : t("CREATE_CAMERA_CLASS")}</h4>
                                <Formik
                                    enableReinitialize
                                    initialValues={initial}
                                    validate={v => { const e = {}; if (!String(v.name || '').trim()) e.name = 'Required'; return e; }}
                                    onSubmit={save}
                                >
                                    {({ handleSubmit, handleChange, values, isSubmitting, errors, touched }) => (
                                        <Form onSubmit={handleSubmit}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t("NAME")}</Form.Label>
                                                <Form.Control name="name" value={values.name} onChange={handleChange} isInvalid={touched.name && !!errors.name} />
                                                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t("DESCRIPTION")}</Form.Label>
                                                <Form.Control as="textarea" name="description" value={values.description} onChange={handleChange} />
                                            </Form.Group>
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <Button type="submit" variant="primary" disabled={isSubmitting}>{editing ? t("UPDATE") : t("SAVE")}</Button>
                                                {editing && <Button type="button" variant="secondary" onClick={() => setEditing(null)}>{t("CANCEL")}</Button>}
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </Col>

                            <Col md={7}>
                                <div style={{ background: '#0A203A', border: '1px solid white', borderRadius: 6, padding: 20, marginLeft: 30, color: 'white', minHeight: 200 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                        <strong>{t("CAMERA_CLASSES_LIST")}</strong>
                                        <Button size="sm" variant="secondary" onClick={load} disabled={loading}>{loading ? t('LOADING') : t('RELOAD')}</Button>
                                    </div>

                                    {list.length === 0 ? (
                                        <div className="text-muted">{t('NO_ITEMS')}</div>
                                    ) : (
                                        <ul className="list-group">
                                            {list.map(item => (
                                                <li
                                                    key={item.id}
                                                    className="list-group-item d-flex justify-content-between align-items-start"
                                                    style={{ paddingRight: 0, paddingTop: 8, paddingBottom: 8 }}
                                                >
                                                    <div className="d-flex" style={{ gap: 12 }}>
                                                        <div className="ms-1">
                                                            <p className="mb-2" style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>
                                                                #{item.id} — {item.name}
                                                            </p>
                                                            {item.description && (
                                                                <p className="mb-1" style={{ margin: 0, fontSize: 16, color: '#aaa' }}>
                                                                    {item.description}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="card_video_archive_list_icons_item" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <button type="button" onClick={() => { setEditing(item); }} title="Edit"
                                                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: '1px solid #ff2d6b' }}>
                                                            <img src={editIcon} alt="edit" />
                                                        </button>
                                                        <button type="button" onClick={() => { remove(item.id); }} title="Delete"
                                                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: '1px solid #ff2d6b', color: '#ff2d6b' }}>
                                                            ✖
                                                        </button>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Container>
            </Fade>
            <Dialog ref={dialogRef} />
        </Fragment>
    );
};

export default CameraClassesTab;
